import { useState, useCallback } from "react";

const STORAGE_KEY = "prezntz-cookie-consent";

interface CookieConsent {
  analytics: boolean;
  advertising: boolean;
  timestamp: string;
}

function getStoredConsent(): CookieConsent | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as CookieConsent;
      // Migrate old format (no advertising field) — default advertising to match analytics
      if (parsed.advertising === undefined) {
        parsed.advertising = parsed.analytics;
        storeConsent(parsed);
      }
      return parsed;
    }
  } catch {
    // localStorage not available or invalid JSON
  }
  return null;
}

function storeConsent(consent: CookieConsent): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // localStorage not available
  }
}

export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsent | null>(getStoredConsent);

  const hasResponded = consent !== null;
  const analyticsAllowed = consent?.analytics ?? false;
  const advertisingAllowed = consent?.advertising ?? false;

  const acceptAll = useCallback(() => {
    const newConsent: CookieConsent = {
      analytics: true,
      advertising: true,
      timestamp: new Date().toISOString(),
    };
    storeConsent(newConsent);
    setConsent(newConsent);
  }, []);

  const acceptEssentialOnly = useCallback(() => {
    const newConsent: CookieConsent = {
      analytics: false,
      advertising: false,
      timestamp: new Date().toISOString(),
    };
    storeConsent(newConsent);
    setConsent(newConsent);
  }, []);

  const declineAll = useCallback(() => {
    const newConsent: CookieConsent = {
      analytics: false,
      advertising: false,
      timestamp: new Date().toISOString(),
    };
    storeConsent(newConsent);
    setConsent(newConsent);
  }, []);

  return {
    consent,
    hasResponded,
    analyticsAllowed,
    advertisingAllowed,
    acceptAll,
    acceptEssentialOnly,
    declineAll,
  };
}
